import { getRuntimeConfig } from './runtimeConfig';

/**
 * Authentication Feature Flags Configuration
 *
 * This module provides a centralized system for managing authentication feature flags
 * that enable gradual rollout and A/B testing of authentication features.
 */

/**
 * Available authentication feature flags
 */
export interface AuthFeatureFlags {
  /** Enable redirect-based authentication flow for Entra ID */
  enableRedirectFlow?: boolean;

  /** Enable custom branded login page */
  enableCustomLoginPage?: boolean;

  /** Enable enhanced error handling with AuthErrorBoundary */
  enableEnhancedErrorHandling?: boolean;

  /** Enable gradual rollout system */
  enableGradualRollout?: boolean;

  /** Percentage of users to include in rollout (0-100) */
  rolloutPercentage?: number;

  /** Enable enhanced loading states and animations */
  enableEnhancedLoadingStates?: boolean;

  /** Enable mobile-optimized authentication flows */
  enableMobileOptimizations?: boolean;

  /** Enable accessibility enhancements */
  enableAccessibilityEnhancements?: boolean;

  /** Enable authentication analytics and telemetry */
  enableAnalytics?: boolean;
}

/**
 * Default feature flag values
 */
const DEFAULT_FEATURE_FLAGS: Required<AuthFeatureFlags> = {
  enableRedirectFlow: false, // Default to popup for backward compatibility
  enableCustomLoginPage: true,
  enableEnhancedErrorHandling: true,
  enableGradualRollout: false,
  rolloutPercentage: 100,
  enableEnhancedLoadingStates: true,
  enableMobileOptimizations: true,
  enableAccessibilityEnhancements: true,
  enableAnalytics: false, // Default disabled for privacy
};

/**
 * Get authentication feature flags from configuration
 *
 * Feature flags can be configured in multiple ways:
 * 1. JSON string in VITE_AUTH_FEATURE_FLAGS environment variable
 * 2. Individual environment variables (e.g., VITE_USE_REDIRECT_AUTH)
 * 3. Runtime configuration overrides
 *
 * @returns Merged feature flags with defaults
 */
export function getAuthFeatureFlags(): AuthFeatureFlags {
  const config = getRuntimeConfig();
  let flags: Partial<AuthFeatureFlags> = {};

  // Try to parse feature flags from JSON string
  try {
    const flagsJson = config.VITE_AUTH_FEATURE_FLAGS as string;
    if (flagsJson) {
      flags = JSON.parse(flagsJson);
    }
  } catch (error) {
    console.warn('Failed to parse VITE_AUTH_FEATURE_FLAGS:', error);
  }

  // Merge with individual environment variables
  const envFlags: Partial<AuthFeatureFlags> = {
    enableRedirectFlow: config.VITE_USE_REDIRECT_AUTH === 'true',
    enableCustomLoginPage: config.VITE_ENABLE_CUSTOM_LOGIN !== 'false',
    enableEnhancedErrorHandling: config.VITE_ENABLE_ERROR_BOUNDARY !== 'false',
    enableGradualRollout: config.VITE_ENABLE_GRADUAL_ROLLOUT === 'true',
    rolloutPercentage: config.VITE_ROLLOUT_PERCENTAGE
      ? parseInt(config.VITE_ROLLOUT_PERCENTAGE as string, 10)
      : undefined,
    enableEnhancedLoadingStates:
      config.VITE_ENABLE_ENHANCED_LOADING !== 'false',
    enableMobileOptimizations:
      config.VITE_ENABLE_MOBILE_OPTIMIZATIONS !== 'false',
    enableAccessibilityEnhancements: config.VITE_ENABLE_A11Y !== 'false',
    enableAnalytics: config.VITE_ENABLE_AUTH_ANALYTICS === 'true',
  };

  // Merge flags: JSON config > environment variables > defaults
  return {
    ...DEFAULT_FEATURE_FLAGS,
    ...envFlags,
    ...flags,
  };
}

/**
 * Check if a specific feature flag is enabled
 *
 * @param flagName - Name of the feature flag to check
 * @returns True if the feature is enabled
 */
export function isFeatureEnabled(flagName: keyof AuthFeatureFlags): boolean {
  const flags = getAuthFeatureFlags();
  return Boolean(flags[flagName]);
}

/**
 * Get rollout percentage for gradual feature deployment
 *
 * @returns Rollout percentage (0-100)
 */
export function getRolloutPercentage(): number {
  const flags = getAuthFeatureFlags();
  const percentage = flags.rolloutPercentage ?? 100;
  return Math.max(0, Math.min(100, percentage));
}

/**
 * Check if user is included in a feature rollout
 *
 * Uses a deterministic hash of the user's session to ensure consistent
 * rollout assignment across page reloads and sessions.
 *
 * @param featureName - Name of the feature for rollout tracking
 * @param percentage - Rollout percentage (defaults to configured value)
 * @returns True if user is included in rollout
 */
export function isUserInRollout(
  featureName: string,
  percentage?: number,
): boolean {
  const flags = getAuthFeatureFlags();

  // If gradual rollout is disabled, include all users
  if (!flags.enableGradualRollout) {
    return true;
  }

  const rolloutPercentage = percentage ?? getRolloutPercentage();

  // Get or create a consistent user identifier for this session
  const storageKey = `auth-rollout-${featureName}`;
  let userId = sessionStorage.getItem(storageKey);

  if (!userId) {
    // Use crypto.getRandomValues for secure random generation
    // This is safe for feature rollout as it's not used for cryptographic purposes
    // but provides better randomness than Math.random()
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    userId = array[0].toString(36).substring(0, 15);
    sessionStorage.setItem(storageKey, userId);
  }

  // Create a deterministic hash of the user ID
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const userPercentage = Math.abs(hash) % 100;
  return userPercentage < rolloutPercentage;
}

/**
 * Feature flag hook for React components
 *
 * @param flagName - Name of the feature flag
 * @returns Current flag value
 */
export function useFeatureFlag(flagName: keyof AuthFeatureFlags): boolean {
  return isFeatureEnabled(flagName);
}
